import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
    ChevronDown,
    ChevronRight,
    ListChecks,
    Loader2,
    Plus,
    RefreshCw,
} from 'lucide-react';

import TaskDefinitionEditor, { TaskDefinition } from './TaskDefinitionEditor';
import TaskDefinitionCreateDialog from './TaskDefinitionCreateDialog';
import TaskDefinitionsList from './TaskDefinitionsList';
import useApi from '../../api';
import AgentTabHeader from './AgentTabHeader';

export interface TaskDefinitionsTabProps {
    agId?: string;
    agentName: string;
}

export default function TaskDefinitionsTab({
    agId,
    agentName: _agentName,
}: TaskDefinitionsTabProps) {
    const { get } = useApi();
    const [taskDefs, setTaskDefs] = useState<TaskDefinition[]>([]);
    const [loading, setLoading] = useState(false);
    const [showHowRecordingWorks, setShowHowRecordingWorks] = useState(false);
    const [showBestPractices, setShowBestPractices] = useState(false);
    const [showTaskDefinitions, setShowTaskDefinitions] = useState(false);
    const [showCreateTaskDefinition, setShowCreateTaskDefinition] = useState(false);

    const fetchTaskDefs = useCallback(async () => {
        if (!agId) return;
        setLoading(true);
        try {
            const res = await get(`/agents/${agId}/task-definitions`);
            setTaskDefs(res.data);
        } catch (err) {
            console.error('Error fetching task definitions:', err);
        } finally {
            setLoading(false);
        }
    }, [agId, get]);

    useEffect(() => {
        fetchTaskDefs();
    }, [fetchTaskDefs]);

    const sortByUsage = (defs: TaskDefinition[]) =>
        [...defs].sort((a, b) => {
            const usageDiff = (b.usage_count ?? 0) - (a.usage_count ?? 0);
            if (usageDiff !== 0) return usageDiff;
            return (b.created_at || '').localeCompare(a.created_at || '');
        });

    const handleUpdated = (updated: TaskDefinition) => {
        setTaskDefs((prev) =>
            prev.map((td) =>
                td.id === updated.id
                    ? { ...updated, usage_count: updated.usage_count ?? td.usage_count ?? 0 }
                    : td
            )
        );
    };

    const handleDeleted = (id: string) => {
        setTaskDefs((prev) => prev.filter((td) => td.id !== id));
    };

    const handleCreated = (created: TaskDefinition) => {
        setTaskDefs((prev) => sortByUsage([{ ...created, usage_count: created.usage_count ?? 0 }, ...prev]));
    };

    return (
        <div className="w-full">
            <AgentTabHeader
                icon={ListChecks}
                title="Task definitions"
                description="Create and refine the step-by-step workflows your agent follows in app mode."
            />

            <div className="mx-auto w-full max-w-4xl py-6">
            {/* Task Definitions Section */}
            <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">
                            Task definitions
                        </h3>
                        {taskDefs.length > 0 && (
                            <Badge variant="outline" className="text-[10px] font-medium text-muted-foreground">
                                {taskDefs.length}
                            </Badge>
                        )}
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchTaskDefs}
                        disabled={loading}
                    >
                        {loading ? (
                            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                        ) : (
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Refresh
                    </Button>
                </div>

                <section className="space-y-3">
                    <div>
                        <h3 className="text-sm font-semibold">Recording guidance</h3>
                        <p className="mt-1 text-xs text-muted-foreground">
                            Learn how to record reliable workflows and refine the generated instructions.
                        </p>
                    </div>
                    <div className="overflow-hidden rounded-lg border bg-card divide-y">
                {/* Recording Workflow Instructions */}
                    <Collapsible
                        open={showHowRecordingWorks}
                        onOpenChange={setShowHowRecordingWorks}
                    >
                        <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-muted/30">
                            <div>
                                <h4 className="text-sm font-medium">How task recording works</h4>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    Record a walkthrough from your app, then generate and refine its steps.
                                </p>
                            </div>
                            {showHowRecordingWorks ? (
                                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t">
                            <div className="p-4">
                                <ol className="list-decimal pl-4 space-y-2 text-xs text-muted-foreground">
                                    <li>
                                        Enter your app URL in the{' '}
                                        <span className="font-medium text-foreground">
                                            Configuration tab
                                        </span>{' '}
                                        under Main App URL.
                                    </li>
                                    <li>
                                        Click{' '}
                                        <span className="font-medium text-foreground">
                                            Record Tasks
                                        </span>
                                        . This opens your app with task recording enabled.
                                    </li>
                                    <li>
                                        Use the record button in the collapsed widget bar to turn on your
                                        mic, then click through the task while narrating what you are doing.
                                    </li>
                                    <li>
                                        Click stop when finished, then click generate to create a task
                                        definition automatically associated with this agent.
                                    </li>
                                    <li>
                                        Review and edit the generated language or step construction as
                                        needed.
                                    </li>
                                    <li>
                                        Record as many tasks as you need. We recommend keeping a single task
                                        definition under 30 clicks/steps.
                                    </li>
                                    <li>
                                        For long or high-level goals, create a high-level task definition
                                        whose steps tell the AI to load and follow specific task definitions.
                                    </li>
                                </ol>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>

                {/* Best Practices Collapsible */}
                    <Collapsible open={showBestPractices} onOpenChange={setShowBestPractices}>
                        <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-4 text-left transition-colors hover:bg-muted/30">
                            <div>
                                <h4 className="text-sm font-medium">Recording best practices</h4>
                                <p className="mt-0.5 text-xs text-muted-foreground">
                                    Capture walkthroughs that generate clear, reusable instructions.
                                </p>
                            </div>
                            {showBestPractices ? (
                                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="border-t">
                            <div className="space-y-4 p-4">
                                <div className="space-y-3 text-xs text-muted-foreground">
                                    <p className="text-sm">
                                        Task definitions are step-by-step instructions your AI agent follows
                                        to guide users through workflows in your application. They are
                                        generated from your recorded walkthroughs.
                                    </p>
                                    <p>
                                        Narrate what you are doing in real time, include as much extra
                                        context as you want, and think about the walkthrough like you are
                                        onboarding a customer to a specific feature or workflow.
                                    </p>

                                    <div className="space-y-2">
                                        <p className="font-medium text-foreground text-sm">
                                            Use [bracket placeholders] for variable values
                                        </p>
                                        <p>
                                            After generation, wrap any value the agent needs to get from the
                                            user in square brackets. This tells the agent to either fill it
                                            from context or ask the user.
                                        </p>
                                        <div className="space-y-1 rounded-md border p-3 font-mono text-[11px]">
                                            <p className="text-green-600 dark:text-green-400">
                                                Good: In the Email field, enter [email address]
                                            </p>
                                            <p className="text-green-600 dark:text-green-400">
                                                Good: Click on [the desired session]
                                            </p>
                                            <p className="text-green-600 dark:text-green-400">
                                                Good: Select [the target environment] from the dropdown
                                            </p>
                                            <p className="text-red-500 dark:text-red-400">
                                                Bad: In the Email field, enter john@example.com
                                            </p>
                                            <p className="text-red-500 dark:text-red-400">
                                                Bad: Click on session ID abc-123
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="font-medium text-foreground text-sm">
                                            Never hard-code user-specific data
                                        </p>
                                        <p>
                                            If a generated step involves clicking a specific record, row, or
                                            item that came from your account (a project name, a report
                                            title, a session ID), replace it with a generic placeholder.
                                            These won't exist in your users' accounts.
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="font-medium text-foreground text-sm">
                                            Keep steps action-oriented
                                        </p>
                                        <p>
                                            Use imperative voice: "Click...", "Enter...", "Navigate to...".
                                            Each step should be one clear action. Aim for 4-18 steps per
                                            task definition.
                                        </p>
                                    </div>

                                    <div className="space-y-2">
                                        <p className="font-medium text-foreground text-sm">
                                            Reference fixed UI elements by name
                                        </p>
                                        <p>
                                            Buttons, tabs, menu items, and other fixed UI labels are fine to
                                            reference by their exact name (e.g., "Click the Save button").
                                            Only use placeholders for dynamic/user-generated content.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                    </div>
                </section>

                {/* Create Task Definition */}
                <div className="rounded-lg border border-dashed p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0 basis-48">
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium">Create task definition</h4>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Write a new task definition and associate it with this agent.
                                </p>
                            </div>
                        </div>
                        <Button
                            onClick={() => setShowCreateTaskDefinition(true)}
                            className="shrink-0"
                            disabled={!agId}
                        >
                            <Plus className="mr-1.5 h-3.5 w-3.5" />
                            Create task definition
                        </Button>
                    </div>
                </div>

                {/* Add Existing Task Definition */}
                <div className="rounded-lg border border-dashed p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0 basis-48">
                            <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium">Add existing task definition</h4>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Link a task definition already created for another agent.
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => setShowTaskDefinitions(true)}
                            className="shrink-0"
                            disabled={!agId}
                        >
                            Browse task definitions
                        </Button>
                    </div>
                </div>

                {/* Task Definitions List */}
                {loading && taskDefs.length === 0 ? (
                    <div className="flex items-center justify-center rounded-lg border border-dashed p-8">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading task definitions...
                        </div>
                    </div>
                ) : taskDefs.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-8 text-center">
                        <div className="flex flex-col items-center gap-2">
                            <p className="text-sm font-medium text-muted-foreground">
                                No task definitions yet
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Create one manually here or use "Record Tasks" in the Configuration tab.
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {taskDefs.map((td) => (
                            <TaskDefinitionEditor
                                key={td.id}
                                taskDef={td}
                                agentId={agId || ''}
                                onUpdated={handleUpdated}
                                onDeleted={handleDeleted}
                            />
                        ))}
                    </div>
                )}
            </div>

            {showTaskDefinitions && agId && (
                <TaskDefinitionsList
                    agentId={agId}
                    setShowTaskDefinitions={setShowTaskDefinitions}
                    onAssociated={fetchTaskDefs}
                />
            )}

            {agId && (
                <TaskDefinitionCreateDialog
                    agentId={agId}
                    open={showCreateTaskDefinition}
                    onOpenChange={setShowCreateTaskDefinition}
                    onCreated={handleCreated}
                />
            )}
            </div>
        </div>
    );
}
